import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LogOut, MonitorDown, Shield } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { getMe } from '@/api/users'
import { useNotifyDisplay } from '@/components/Notify'
import { getVisibleNavItems } from './nav-items'
import { useHeaderActionValue } from './HeaderAction'
import './TopBar.css'

// Диалог установки тянет qr-code-styling — грузим лениво, чтобы не раздувать главный чанк.
const InstallAppDialog = lazy(() =>
  import('@/components/InstallAppDialog').then((m) => ({ default: m.InstallAppDialog })),
)

export function TopBar() {
  const { logout, isAdmin } = useAuth()
  const navItems = getVisibleNavItems(isAdmin)
  const action = useHeaderActionValue()
  const notice = useNotifyDisplay()
  const [installOpen, setInstallOpen] = useState(false)
  const { pathname } = useLocation()

  // installLink для диалога установки приложения.
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: Infinity })

  // Навигация прокручивается горизонтально, когда пункты не влезают: действие раздела и
  // кнопки установки/выхода при этом остаются на месте — они не должны уезжать из виду.
  // Затухание рисуем только с той стороны, куда реально есть что мотать, иначе размытый
  // край врал бы про наличие скрытых пунктов.
  const navRef = useRef<HTMLElement | null>(null)
  const [edges, setEdges] = useState({ start: false, end: false })

  const syncEdges = useCallback(() => {
    const nav = navRef.current
    if (!nav) return
    const max = nav.scrollWidth - nav.clientWidth
    setEdges({ start: nav.scrollLeft > 1, end: nav.scrollLeft < max - 1 })
  }, [])

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    syncEdges()
    // Ширина меняется и без ресайза окна: кнопка действия раздела приходит и уходит.
    const observer = new ResizeObserver(syncEdges)
    observer.observe(nav)
    return () => observer.disconnect()
  }, [syncEdges, action, navItems.length])

  // Активный пункт после перехода мог остаться за краем прокрутки — подтягиваем его в кадр.
  useEffect(() => {
    const active = navRef.current?.querySelector('.topbar__nav-link.is-active')
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    syncEdges()
  }, [pathname, syncEdges])

  return (
    <header className="topbar">
      <div className="topbar__brand">
        <div className="topbar__mark">
          <Shield fill="currentColor" strokeWidth={0} aria-hidden />
        </div>
        <div className="topbar__brand-flip">
          <div className="topbar__brand-flip-inner">
            <span className="topbar__brand-face topbar__brand-face--front">
              <span className="topbar__brand-name">box-config</span>
            </span>
            <span className="topbar__brand-face topbar__brand-face--back">
              <span className="topbar__brand-versions">
                <span className="topbar__brand-version">ui v{__APP_VERSION__}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
      <div
        className={`topbar__nav-wrap${edges.start ? ' has-start' : ''}${edges.end ? ' has-end' : ''}`}
      >
        <nav className="topbar__nav" ref={navRef} onScroll={syncEdges}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              aria-label={item.label}
              className={({ isActive }) => `topbar__nav-link${isActive ? ' is-active' : ''}`}
            >
              <span className="topbar__nav-icon" aria-hidden>
                {item.icon}
              </span>
              <span className="topbar__nav-text">
                [{item.shortcut}] {item.label}
              </span>
            </NavLink>
          ))}
        </nav>
        <span className="topbar__nav-fade topbar__nav-fade--start" aria-hidden />
        <span className="topbar__nav-fade topbar__nav-fade--end" aria-hidden />
      </div>
      <div className="topbar__right">
        {action ? <div className="topbar__action">{action}</div> : null}
        <button
          className="topbar__admin-btn"
          onClick={() => setInstallOpen(true)}
          title="Установить приложение"
          aria-label="Установить приложение"
        >
          <MonitorDown size={14} aria-hidden />
        </button>
        <button className="topbar__user" onClick={logout} title="Выйти" aria-label="Выйти">
          <LogOut size={14} aria-hidden />
        </button>
      </div>
      {notice ? (
        // key перезапускает flip-анимацию для каждого нового уведомления.
        <div
          key={notice.id}
          className={`topbar__notify topbar__notify--${notice.severity}`}
          role="status"
          aria-live={notice.severity === 'error' ? 'assertive' : 'polite'}
        >
          {notice.message}
        </div>
      ) : null}
      {user && installOpen ? (
        <Suspense fallback={null}>
          <InstallAppDialog
            open={installOpen}
            onClose={() => setInstallOpen(false)}
            installLink={user.installLink}
          />
        </Suspense>
      ) : null}
    </header>
  )
}
